import { useState } from "react";
import { Link } from "react-router-dom";
import MSG91OtpButton from "@/components/MSG91OtpButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Smartphone } from "lucide-react";

/**
 * Partner (hotel) signup — OTP + mandatory Hotel name, Contact person, Hotel
 * email captured BEFORE the OTP flow starts (Iter26). Backend enforces
 * "already registered → 409".
 */
export default function RegisterHotel() {
  const [hotelName, setHotelName] = useState("");
  const [contactName, setContactName] = useState("");
  const [hotelEmail, setHotelEmail] = useState("");

  const filled =
    hotelName.trim().length >= 2 &&
    contactName.trim().length >= 2 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(hotelEmail.trim());

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-10">
      <div className="max-w-5xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <div className="hw-label text-blue-700">For Hotels · Sign up</div>
          <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3 leading-tight">
            Onboard your hotel.<br />Start receiving leads.
          </h1>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Fill hotel details + your mobile — verify OTP — complete your listing on the next screen. Admin approves in 24-48 hrs.
          </p>
        </div>

        <div className="lg:col-span-7">
          <div data-testid="register-hotel-otp-card" className="hw-card p-7 hw-shadow-pop-amber space-y-4">
            <div className="hw-label text-blue-700">Step 1 of 2 — Create partner account</div>
            <h2 className="hw-head text-2xl md:text-3xl font-black text-slate-900">Sign up your hotel</h2>

            <div>
              <Label className="hw-label">Hotel name *</Label>
              <Input value={hotelName} onChange={(e) => setHotelName(e.target.value)} data-testid="reg-hotel-name"
                className="rounded-sm mt-1" placeholder="e.g. The Marigold Grand, Bangalore" />
            </div>
            <div>
              <Label className="hw-label">Contact person name *</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="reg-hotel-contact-name"
                className="rounded-sm mt-1" placeholder="Your name" autoComplete="name" />
            </div>
            <div>
              <Label className="hw-label">Hotel email *</Label>
              <Input type="email" value={hotelEmail} onChange={(e) => setHotelEmail(e.target.value.trim())} data-testid="reg-hotel-email"
                className="rounded-sm mt-1" placeholder="reservations@yourhotel.com" inputMode="email" />
            </div>

            <MSG91OtpButton
              intent="partner"
              mode="signup"
              signupDetails={{
                name: contactName.trim(),
                email: hotelEmail.trim().toLowerCase(),
                hotel_name: hotelName.trim(),
                contact_name: contactName.trim(),
                hotel_email: hotelEmail.trim().toLowerCase(),
              }}
              disabled={!filled}
              label={filled ? "Verify mobile OTP to sign up" : "Fill all fields first"}
            />

            <div className="hw-card bg-slate-50 border-slate-200 p-3 flex items-start gap-3">
              <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-slate-600 leading-relaxed">
                <span className="font-bold text-slate-800">Pending admin approval:</span> after OTP you&apos;ll add hotel photos + documents. Admin approves in 24-48 hrs.
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-slate-500">
              <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Already registered? <Link to="/login" className="text-blue-700 hover:underline font-semibold">Log in as Hotel</Link>.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
